/*
 * <categories-drawer> — "Shop All Categories" slide-out menu.
 *
 * Light-DOM custom element, rendered once (layout/theme.liquid). Any element on
 * the page carrying [data-categories-drawer-open] opens it; the close button,
 * the scrim and the Escape key close it. Self-contained scroll-lock + focus
 * trap — no dependency on the theme's empire.js bundle.
 */
(function () {
  'use strict';

  if (customElements.get('categories-drawer')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CategoriesDrawer extends HTMLElement {
    connectedCallback() {
      this.scrim = this.querySelector('[data-categories-drawer-scrim]');
      this.dialog = this.querySelector('.jns-cat-drawer');
      this.closeBtn = this.querySelector('[data-categories-drawer-close]');
      this.lastFocus = null;
      this.isOpen = false;

      if (!this.scrim || !this.dialog || !this.closeBtn) return;

      this._onDocClick = this._onDocClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);

      document.addEventListener('click', this._onDocClick);
      this.scrim.addEventListener('click', this.close);
      this.closeBtn.addEventListener('click', this.close);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
      document.documentElement.classList.remove('categories-drawer-open');
    }

    _onDocClick(event) {
      const target = event.target;
      if (!target || !target.closest) return;
      const trigger = target.closest('[data-categories-drawer-open]');
      if (!trigger) return;
      event.preventDefault();
      this.lastFocus = trigger;
      this.open();
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.scrim.classList.add('is-open');
      this.dialog.classList.add('is-open');
      this.dialog.removeAttribute('inert');
      this.dialog.setAttribute('aria-hidden', 'false');
      this.scrim.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('categories-drawer-open');
      this._setExpanded(true);
      document.addEventListener('keydown', this._onKeydown);

      const closeBtn = this.closeBtn;
      requestAnimationFrame(() => {
        try {
          closeBtn.focus();
        } catch (e) {
          /* element not focusable yet — ignore */
        }
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.scrim.classList.remove('is-open');
      this.dialog.classList.remove('is-open');
      this.dialog.setAttribute('inert', '');
      this.dialog.setAttribute('aria-hidden', 'true');
      this.scrim.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('categories-drawer-open');
      this._setExpanded(false);
      document.removeEventListener('keydown', this._onKeydown);

      if (this.lastFocus && this.lastFocus.isConnected) {
        try {
          this.lastFocus.focus();
        } catch (e) {
          /* ignore */
        }
      }
      this.lastFocus = null;
    }

    _setExpanded(state) {
      document
        .querySelectorAll('[data-categories-drawer-open]')
        .forEach((trigger) => {
          trigger.setAttribute('aria-expanded', state ? 'true' : 'false');
        });
    }

    _onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = Array.from(
        this.dialog.querySelectorAll(FOCUSABLE)
      ).filter(
        (el) =>
          el.offsetWidth > 0 ||
          el.offsetHeight > 0 ||
          el === document.activeElement
      );
      if (!nodes.length) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (!this.dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('categories-drawer', CategoriesDrawer);
})();
